/**
 * The one rule for an invoice's figures (owner, 2026-11-04: "one shared rule"). Used by the server (ticket total,
 * customer page, printed invoices) and imported as is by the staff portal (apps/portal/src/pricing.ts), so the
 * portal, the customer page and what is charged always agree. No imports: it must build in both places.
 *
 *   items       quantity × (price − discount), per line
 *   insurance   above the free amount ($100), the insurance rate (3%) of the declared value, to the cent
 *   tax         Wisconsin sales tax on items + insurance, to the cent, when the customer pays tax and lives in WI
 *   refunds     refunds made after the ticket was paid in full (goodwill, partial): they lower the invoice, so it
 *               stays paid in full (owner, 2026-11-04)
 *   total       items + insurance + tax − refunds
 */
export interface PriceRates { freeInsuranceAmount: number; insuranceRate: number; wisconsinTaxRate: number }
export interface PriceLine { quantity: number; price: number | string; discount?: number | string | null }
export interface InvoiceSums { items: number; insurance: number; subtotal: number; tax: number; refunds: number; total: number }

export const cents = (n: number) => Math.round(n * 100) / 100;

export function invoiceSums(lines: PriceLine[], insured: number, taxed: boolean, rates: PriceRates, refunds = 0): InvoiceSums {
  const items = cents(lines.reduce((s, l) => s + l.quantity * (Number(l.price) - Number(l.discount ?? 0)), 0));
  const insurance = insured > rates.freeInsuranceAmount ? cents(insured * rates.insuranceRate) : 0;
  const subtotal = cents(items + insurance);
  const tax = taxed ? cents(subtotal * rates.wisconsinTaxRate) : 0;
  const r = cents(refunds);
  return { items, insurance, subtotal, tax, refunds: r, total: cents(subtotal + tax - r) };
}

/** What is still due: total − net paid, never below zero. Paid in full when this is 0. */
export const balanceDue = (total: number, paid: number) => Math.max(0, cents(total - paid));

/**
 * Wisconsin, however it was typed: "WI", "Wis", "Wisc", "Wisconsin", in any case, with spaces or periods
 * (owner, 2026-11-04). Addresses are stored as "WI" (database trigger, migration 0024); this also reads old rows.
 */
export function isWisconsin(state: string | null | undefined): boolean {
  return ["WI", "WIS", "WISC", "WISCONSIN"].includes((state ?? "").replace(/[\s.]/g, "").toUpperCase());
}

/** The state as stored: Wisconsin spellings become "WI"; anything else is kept as typed (trimmed). */
export function normalizeState(state: string): string {
  return isWisconsin(state) ? "WI" : state.trim();
}
